"use client";
  
import Link from "next/link";
import { ArrowRight, TrendingUp, Sparkles } from "lucide-react";
import { useState } from "react";
import { Vendor } from "@/lib/types/vendor";
import CompactRestaurantCard from "./CompactRestaurantCard";

interface FeaturedRestaurantsClientProps {
  vendors: Vendor[];
}

export default function FeaturedRestaurantsClient({ 
  vendors 
}: FeaturedRestaurantsClientProps) {
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);

  return (
    <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 mb-4 bg-gradient-to-r from-amber-50 to-orange-50 px-4 py-2 rounded-full">
            <Sparkles className="w-4 h-4 text-amber-500" />
            <span className="text-sm font-semibold text-amber-700 uppercase tracking-wide">
              Curated Selection
            </span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Featured <span className="bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-transparent">Restaurants</span>
          </h2>
          
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Discover handpicked restaurants offering exceptional dining experiences
          </p>
        </div>
<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 p-4">
          {vendors.map((vendor, index) => (
            <div
              key={vendor.id}
              onMouseEnter={() => setHoveredCard(index)}
              onMouseLeave={() => setHoveredCard(null)}
              className="transform transition-all duration-300"
              style={{
                transform: hoveredCard === index ? 'translateY(-4px)' : 'none',
                transitionDelay: hoveredCard === index ? '0ms' : `${index * 50}ms`
              }}
            >
              <CompactRestaurantCard
                vendor={vendor}
                index={index} 
              />
            </div>
          ))}
        </div>

        {/* See More CTA */}
        <div className="text-center">
          <div className="inline-flex flex-col items-center gap-4">
            <Link
              href="/vendors"
              className="group relative inline-flex items-center justify-center gap-3 px-8 py-4 bg-white text-gray-900 font-bold rounded-2xl border-2 border-gray-200 hover:border-amber-500 hover:text-amber-600 hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] overflow-hidden"
            >
              {/* Background effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-amber-500/0 via-amber-500/10 to-amber-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              
              {/* Content */}
              <span className="relative z-10">Explore All Restaurants</span>
              <ArrowRight className="w-5 h-5 relative z-10 group-hover:translate-x-1 transition-transform" />
              
              {/* Ripple effect */}
              <div className="absolute inset-0 overflow-hidden">
                <div className="absolute -inset-1 bg-gradient-to-r from-amber-400/20 to-orange-400/20 opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-500" />
              </div>
            </Link>
            
            <p className="text-sm text-gray-500 flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              <span>{vendors.length}+ premium restaurants available</span>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}